import { isKnownEventType } from '../events/index.ts'
import type { EventRecord } from '../events/index.ts'
import type { NodeIssue, NodeIssueCode } from './types.ts'

/**
 * The rule grammar: Directus filter-rule JSON over declared fields, evaluated by
 * a hand-rolled pure function (ADR-0007 §2).
 *
 * It is hand-rolled rather than compiled to a schema library, and that is the
 * one place this folder is deliberately less convenient than its donor.
 * Directus compiles each rule to Joi per evaluation, and the price is that
 * `_some`, `_none`, `_json` and the geometry operators fall through to
 * `Joi.any()` and therefore **always pass** (study §6). A predicate that
 * silently means "yes" is worse than one that errors, so the operator set here
 * is closed and an unknown operator is a save-time issue.
 */

/* -------------------------------------------------------------- the grammar */

/** The closed v1 operator set. Widening it is a deliberate act (ADR-0007 §11). */
export type RuleOperator = '_eq' | '_neq' | '_in' | '_nin' | '_null' | '_nnull'

/**
 * Closed at compile time in both directions, like `NODE_TYPE_COVERAGE`: the
 * value is the operand shape the operator takes, which is also what
 * {@link validateRule} checks.
 */
const RULE_OPERATOR_COVERAGE: Record<RuleOperator, 'scalar' | 'list' | 'boolean'> = {
  _eq: 'scalar',
  _neq: 'scalar',
  _in: 'list',
  _nin: 'list',
  _null: 'boolean',
  _nnull: 'boolean',
}

export const RULE_OPERATORS: readonly RuleOperator[] = Object.keys(
  RULE_OPERATOR_COVERAGE,
) as RuleOperator[]

export type RuleLogic = '_and' | '_or'
export const RULE_LOGIC: readonly RuleLogic[] = ['_and', '_or'] as const

export type RuleScalar = string | number | boolean | null

export type RuleValue = RuleScalar | readonly RuleScalar[] | readonly TriggerRule[] | TriggerRule

/**
 * A rule as stored: `{ toStatusId: { _eq: "st_paid" } }`,
 * `{ _and: [ … ] }`, or a nested scope `{ $sale: { openCardsIn: { … } } }`.
 *
 * The type is an index signature rather than a discriminated union because the
 * value is read back out of a JSON blob. {@link validateRule} is what makes a
 * stored rule trustworthy, and {@link evaluateRule} still narrows defensively
 * on top of it — a graph can outlive the registry that validated it.
 */
export interface TriggerRule {
  readonly [field: string]: RuleValue
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const isScalar = (v: unknown): v is RuleScalar =>
  v === null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'

const isOperator = (k: string): k is RuleOperator =>
  Object.prototype.hasOwnProperty.call(RULE_OPERATOR_COVERAGE, k)

const isLogic = (k: string): k is RuleLogic => k === '_and' || k === '_or'

/** Every key a rule may not use as a field name: they are the grammar itself. */
const isGrammarKey = (k: string): boolean => isOperator(k) || isLogic(k) || k.startsWith('_')

/* ------------------------------------------------------------ normalisation */

/**
 * Rewrite a rule into the canonical single-key form (ADR-0007 §2): a multi-key
 * object becomes an explicit `_and`, and a bare scalar becomes `_eq`.
 *
 * Normalising at save is what lets everything downstream — the drain, the
 * console's rule editor, a diff between two saves — read one shape instead of
 * three. The `_eq` shorthand is accepted because the console's raw-JSON field
 * will produce `{ toStatusId: "st_paid" }` and refusing it would be pedantry
 * about a form the grammar can express exactly.
 *
 * Returns `null` for a value this grammar cannot express, rather than throwing
 * or quietly returning a rule that means "always". A caller normalises after
 * {@link validateRule} has passed, so `null` there means the two disagree — and
 * a silent `{ _and: [] }` would be the "always passes" failure this module
 * exists to refuse.
 */
export function normaliseRule(rule: unknown): TriggerRule | null {
  if (!isPlainObject(rule)) return null
  const entries = Object.entries(rule)
  if (entries.length === 0) return { _and: [] }

  const normalisedEntries: TriggerRule[] = []
  for (const [key, value] of entries) {
    const one = normaliseEntry(key, value)
    if (one === null) return null
    normalisedEntries.push(one)
  }

  return normalisedEntries.length === 1 ? normalisedEntries[0] : { _and: normalisedEntries }
}

function normaliseEntry(key: string, value: unknown): TriggerRule | null {
  if (isLogic(key)) {
    if (!Array.isArray(value)) return null
    const branches: TriggerRule[] = []
    for (const branch of value) {
      const one = normaliseRule(branch)
      if (one === null) return null
      branches.push(one)
    }
    return { [key]: branches }
  }

  if (isOperator(key)) {
    // A leaf reached without a field in front of it — `{ _eq: 1 }` at the top
    // level names nothing to compare.
    return null
  }

  if (isGrammarKey(key)) return null

  // `{ field: "paid" }` is the shorthand for `{ field: { _eq: "paid" } }`.
  if (isScalar(value)) return { [key]: { _eq: value } }

  if (!isPlainObject(value)) return null

  const keys = Object.keys(value)
  const operatorKeys = keys.filter((k) => k.startsWith('_') && !isLogic(k))

  if (operatorKeys.length === 0) {
    // A scope: `{ $sale: { openCardsIn: … } }`.
    const inner = normaliseRule(value)
    return inner === null ? null : { [key]: inner }
  }

  // A leaf. It may not also carry field names — the two would need different
  // subjects — and every operator on it must be one this build knows.
  if (operatorKeys.length !== keys.length || !operatorKeys.every(isOperator)) return null

  if (operatorKeys.length === 1) return { [key]: value as TriggerRule }

  // Several operators on one field are an implicit `_and` over that field;
  // canonical form makes it explicit, one operator per leaf.
  return {
    _and: operatorKeys.map((op) => ({ [key]: { [op]: value[op] as RuleScalar } })),
  }
}

/* -------------------------------------------------------------- validation */

/**
 * `field` is the dotted path the problem sits at. At the top of a rule there is
 * no path, and the issue is about the rule itself — so the key is **omitted**
 * rather than spelled `''`. {@link NodeIssue} declares one absence, and a
 * caller that prefixes these paths (`nodes.ts`'s `rule` field) would otherwise
 * produce `rule.` for the empty one.
 */
const issue = (code: NodeIssueCode, field: string, message: string): NodeIssue =>
  field === '' ? { code, message } : { code, field, message }

/**
 * Check a rule against the grammar, at save time.
 *
 * `field` on each issue is the dotted path the problem sits at, so the console
 * can point at it in the raw-JSON editor rather than saying "the rule is
 * invalid".
 */
export function validateRule(rule: unknown, at = ''): NodeIssue[] {
  if (!isPlainObject(rule)) {
    return [issue('invalid_rule', at, 'a rule must be an object')]
  }

  const issues: NodeIssue[] = []
  for (const [key, value] of Object.entries(rule)) {
    const path = at === '' ? key : `${at}.${key}`

    if (isLogic(key)) {
      if (!Array.isArray(value)) {
        issues.push(issue('invalid_rule', path, `${key} takes an array of rules`))
        continue
      }
      value.forEach((branch, i) => issues.push(...validateRule(branch, `${path}[${i}]`)))
      continue
    }

    if (isOperator(key)) {
      issues.push(
        issue('invalid_rule', path, `${key} needs a field in front of it, not a bare rule`),
      )
      continue
    }

    if (key.startsWith('_')) {
      issues.push(issue('unknown_operator', path, `unknown operator ${key}`))
      continue
    }

    issues.push(...validateFieldValue(value, path))
  }

  return issues
}

function validateFieldValue(value: unknown, path: string): NodeIssue[] {
  if (isScalar(value)) return [] // the `_eq` shorthand
  if (!isPlainObject(value)) {
    return [issue('invalid_rule', path, 'a field takes a scalar, an operator, or a nested rule')]
  }

  const keys = Object.keys(value)
  const operatorKeys = keys.filter((k) => k.startsWith('_') && !isLogic(k))
  if (operatorKeys.length === 0) return validateRule(value, path)

  // An operator object is a leaf: it may not mix operators with field names,
  // because the two would need different subjects.
  const issues: NodeIssue[] = []
  for (const key of keys) {
    const leafPath = `${path}.${key}`
    if (!key.startsWith('_')) {
      issues.push(issue('invalid_rule', leafPath, 'a leaf may not mix operators with fields'))
      continue
    }
    if (!isOperator(key)) {
      issues.push(issue('unknown_operator', leafPath, `unknown operator ${key}`))
      continue
    }
    issues.push(...validateOperand(key, (value as Record<string, unknown>)[key], leafPath))
  }
  return issues
}

function validateOperand(op: RuleOperator, operand: unknown, path: string): NodeIssue[] {
  switch (RULE_OPERATOR_COVERAGE[op]) {
    case 'scalar':
      return isScalar(operand) ? [] : [issue('invalid_rule', path, `${op} takes a scalar`)]
    case 'list':
      return Array.isArray(operand) && operand.every(isScalar)
        ? []
        : [issue('invalid_rule', path, `${op} takes an array of scalars`)]
    case 'boolean':
      return typeof operand === 'boolean'
        ? []
        : [issue('invalid_rule', path, `${op} takes true or false`)]
  }
}

/* --------------------------------------------------------------- the paths */

/**
 * Every dotted field path a rule names, in document order.
 *
 * `validateFlowGraph` needs them for the two checks it can make about a rule's
 * *subject* rather than its shape: that a `$sale.openCardsIn.<id>` path names a
 * pipeline the graph mentions, and that a `<key>.…` path names an upstream
 * node.
 */
export function rulePathsIn(rule: unknown, at = ''): string[] {
  if (!isPlainObject(rule)) return []
  const paths: string[] = []

  for (const [key, value] of Object.entries(rule)) {
    if (isLogic(key)) {
      if (Array.isArray(value)) for (const branch of value) paths.push(...rulePathsIn(branch, at))
      continue
    }
    if (key.startsWith('_')) continue

    const path = at === '' ? key : `${at}.${key}`
    // `_and` and `_or` are grammar, not operators: a scope whose only key is
    // one of them still holds fields underneath. Reading it as a leaf handed
    // `validateFlowGraph` a bare `$sale`, and the ADR-0007 §7 pipeline check
    // — which only fires on `$sale.openCardsIn.<id>` — silently did not run.
    if (isPlainObject(value) && Object.keys(value).some((k) => !k.startsWith('_') || isLogic(k))) {
      paths.push(...rulePathsIn(value, path))
    } else {
      paths.push(path)
    }
  }

  return paths
}

/* -------------------------------------------------------------- evaluation */

/**
 * The verdict of one rule against one subject.
 *
 * It is not a `boolean`, and that is the whole point of ADR-0007 §6's two error
 * channels. A rule that is **false** takes the `reject` edge; a rule the
 * evaluator **cannot apply** — an unknown operator, a shape the grammar does
 * not describe — traverses no edge at all: the step is `error` and the run
 * fails, unretried, because re-reading the same graph against the same event
 * produces the same verdict. Directus branches by throwing for both, so a false
 * rule and a misconfigured rule take the same edge there (study §6) — which is
 * precisely the confusion this split removes.
 */
export type RuleVerdict =
  | { ok: true; value: boolean }
  | { ok: false; code: NodeIssueCode; path: string; message: string }

const unevaluable = (code: NodeIssueCode, path: string, message: string): RuleVerdict => ({
  ok: false,
  code,
  path,
  message,
})

/**
 * Evaluate a rule against a subject.
 *
 * **A missing field is `false`**, copied verbatim from Directus (ADR-0007 §2)
 * and blanket across the operator set — a rule can never fire on data it cannot
 * see. The consequence worth stating rather than discovering: `_null` is not an
 * existence check. `{ externalRef: { _null: true } }` is true when the field is
 * present and `null`, and **false** when the key is absent, which on a partial
 * event payload is the same absence a person would have called "null". "Is this
 * key absent" is deliberately not expressible in v1: it would need a seventh
 * operator, and widening the set is its own act (ADR-0007 §11).
 *
 * **That example is already live in this repo, and the answer depends on which
 * writer made the card** (#194). `facade.prepareCreateLead` omits `externalRef`
 * for a card typed into Ludus, while `adapters/home.ts` writes it — so
 * `{ externalRef: { _null: true } }` distinguishes *writers*, not data. It is
 * verbatim Directus and §2 asks for verbatim, so this is not a defect; it is a
 * trap a flow author would otherwise find by watching a rule behave differently
 * on two cards that look identical on the board.
 *
 * A multi-key object is evaluated as `_and` even though {@link normaliseRule}
 * would have rewritten it, because a rule stored before normalisation shipped
 * must still evaluate to what it plainly says.
 */
export function evaluateRule(rule: unknown, subject: unknown, at = ''): RuleVerdict {
  if (!isPlainObject(rule)) {
    return unevaluable('invalid_rule', at, 'a rule must be an object')
  }

  const entries = Object.entries(rule)
  // `{}` and `{ _and: [] }` are vacuously true: no rule is every event.
  if (entries.length === 0) return { ok: true, value: true }

  let value = true
  for (const [key, operand] of entries) {
    const path = at === '' ? key : `${at}.${key}`
    const verdict = evaluateEntry(key, operand, subject, path)
    if (!verdict.ok) return verdict
    // Multi-key is `_and`: keep walking so an unevaluable sibling is still
    // reported rather than short-circuited away by an earlier `false`.
    value = value && verdict.value
  }
  return { ok: true, value }
}

function evaluateEntry(key: string, operand: unknown, subject: unknown, path: string): RuleVerdict {
  if (isLogic(key)) {
    if (!Array.isArray(operand)) {
      return unevaluable('invalid_rule', path, `${key} takes an array of rules`)
    }
    let acc = key === '_and'
    // `[i]`, the same convention `validateRule` uses. `RuleVerdict.path` is
    // what the console and the failing step point at, so two branches faulting
    // on the same field have to be distinguishable.
    for (const [index, branch] of operand.entries()) {
      const verdict = evaluateRule(branch, subject, `${path}[${index}]`)
      if (!verdict.ok) return verdict
      acc = key === '_and' ? acc && verdict.value : acc || verdict.value
    }
    return { ok: true, value: acc }
  }

  if (isOperator(key)) {
    return unevaluable('invalid_rule', path, `${key} needs a field in front of it`)
  }

  if (key.startsWith('_')) {
    return unevaluable('unknown_operator', path, `unknown operator ${key}`)
  }

  const present = isPlainObject(subject) && Object.prototype.hasOwnProperty.call(subject, key)
  const held = present ? (subject as Record<string, unknown>)[key] : undefined

  if (isScalar(operand)) {
    return { ok: true, value: present && held === operand }
  }

  if (!isPlainObject(operand)) {
    return unevaluable('invalid_rule', path, 'a field takes a scalar, an operator, or a rule')
  }

  const operatorKeys = Object.keys(operand).filter((k) => k.startsWith('_') && !isLogic(k))
  if (operatorKeys.length === 0) {
    // A nested scope: `{ $sale: { openCardsIn: … } }`. A subject that does not
    // hold the key still evaluates its children, so every leaf below reports
    // the same "missing ⇒ false" rather than the scope swallowing them.
    return evaluateRule(operand, present ? held : undefined, path)
  }

  let value = true
  for (const [op, rhs] of Object.entries(operand)) {
    const leafPath = `${path}.${op}`
    if (!op.startsWith('_')) {
      return unevaluable('invalid_rule', leafPath, 'a leaf may not mix operators with fields')
    }
    if (!isOperator(op)) {
      return unevaluable('unknown_operator', leafPath, `unknown operator ${op}`)
    }
    const verdict = applyOperator(op, rhs, present, held, leafPath)
    if (!verdict.ok) return verdict
    value = value && verdict.value
  }
  return { ok: true, value }
}

function applyOperator(
  op: RuleOperator,
  operand: unknown,
  present: boolean,
  held: unknown,
  path: string,
): RuleVerdict {
  const shape = RULE_OPERATOR_COVERAGE[op]
  if (shape === 'scalar' && !isScalar(operand)) {
    return unevaluable('invalid_rule', path, `${op} takes a scalar`)
  }
  if (shape === 'list' && !(Array.isArray(operand) && operand.every(isScalar))) {
    return unevaluable('invalid_rule', path, `${op} takes an array of scalars`)
  }
  if (shape === 'boolean' && typeof operand !== 'boolean') {
    return unevaluable('invalid_rule', path, `${op} takes true or false`)
  }

  // The blanket rule, applied once, before any operator gets to see the value.
  if (!present) return { ok: true, value: false }

  switch (op) {
    case '_eq':
      return { ok: true, value: held === operand }
    case '_neq':
      return { ok: true, value: held !== operand }
    case '_in':
      return { ok: true, value: (operand as readonly RuleScalar[]).includes(held as RuleScalar) }
    case '_nin':
      return { ok: true, value: !(operand as readonly RuleScalar[]).includes(held as RuleScalar) }
    case '_null':
      return { ok: true, value: (held === null) === operand }
    case '_nnull':
      return { ok: true, value: (held !== null) === operand }
  }
}

/* ------------------------------------------------------- the trigger side */

/**
 * The flat view of a stored event a trigger rule is written against: the row's
 * own columns plus the declared fields of the payload.
 *
 * `null` when this build does not know the type. The payload of an unknown type
 * has no declared fields to write a rule against, so evaluating one would be
 * guessing at a shape — and the drain has already filtered on `event_type`, so
 * a row reaching here with an unknown type did not match anyway.
 *
 * The columns win over the payload where both carry a field. They are what the
 * writer actually stored; the payload is a copy of the same fact and a row old
 * enough to disagree should be read from the column.
 */
export function eventSubject(record: EventRecord): Record<string, unknown> | null {
  if (!isKnownEventType(record.type)) return null
  const payload = isPlainObject(record.payload) ? record.payload : {}
  return {
    ...payload,
    id: record.id,
    pipelineId: record.pipelineId,
    leadId: record.leadId,
    type: record.type,
    actor: record.actor,
    occurredAt: record.occurredAt,
    seq: record.seq,
    correlationId: record.correlationId,
    causationId: record.causationId,
    causeDepth: record.causeDepth,
  }
}

/**
 * Does this stored event satisfy this trigger's `match`?
 *
 * The second tier of ADR-0007 §2's matching: the drain selects
 * `WHERE pipeline_id = ? AND event_type = ? AND enabled = 1` off the index, then
 * asks this per surviving row.
 *
 * It collapses to `false` where {@link evaluateRule} would say "unevaluable",
 * and the direction is deliberate: a trigger that cannot be evaluated must not
 * fire, because firing would run the action on every event of its type. It
 * cannot normally happen — `validateFlowGraph` rejects unknown operators at
 * save — so reaching it means the graph outlived the registry that validated
 * it. A caller that needs to *report* that rather than skip it calls
 * {@link evaluateRule} directly; the condition node does exactly that, because
 * there an unevaluable rule fails the run loudly (ADR-0007 §6).
 */
export function matchesTrigger(rule: unknown, record: EventRecord): boolean {
  const subject = eventSubject(record)
  if (subject === null) return false
  const verdict = evaluateRule(rule, subject)
  return verdict.ok && verdict.value
}
